// Phase 2 "fast media": Change thumbnail (from the project's ••• menu). Scrub the version's video to the moment you
// want on its card and keep it, or hand the choice back to the studio (which picks a strong frame, never the first).
import { useEffect, useRef, useState } from "react";
import { api } from "../api";
import { director } from "../director";
import { fmtFrameTime } from "../library";
import { bump, toast } from "../store";
import type { Project, Version } from "../types";
import { Sheet } from "./bits";
import { Download } from "./Download";

export function ThumbnailSheet({ p, v, onClose }: { p: Project; v: Version; onClose: () => void }) {
  const video = useRef<HTMLVideoElement>(null);
  const max = Math.max(0.1, v.durationS || 0);
  const [t, setT] = useState(() => Math.min(max, v.posterAtS ?? max * 0.35));
  const [ready, setReady] = useState(false);
  const [busy, setBusy] = useState<"pick" | "auto" | null>(null);

  // show the frame under the slider (a seek; nothing plays)
  useEffect(() => {
    const el = video.current;
    if (el && ready && Math.abs(el.currentTime - t) > 0.02) el.currentTime = t;
  }, [t, ready]);

  const save = async (atS: number | null) => {
    setBusy(atS === null ? "auto" : "pick");
    try {
      const r = await api.setThumbnail(p.id, { version: v.version, atS });
      bump("project", "projects", "library");
      director.context(`The user ${atS === null ? "let the studio pick the thumbnail" : `set the thumbnail to the frame at ${fmtFrameTime(r.posterAtS ?? atS)}`} for v${v.version} of "${p.title}".`);
      toast(atS === null ? "The studio picked the thumbnail." : "Thumbnail saved.", "good");
      onClose();
    } catch (e) {
      toast(`Couldn't change the thumbnail. ${(e as Error).message}`, "bad");
      setBusy(null);
    }
  };

  return (
    <Sheet title="Change thumbnail" onClose={onClose} className="thumb-sheet">
      <div className="sheet-body">
        <p className="muted">Slide to the moment you want on the card for v{v.version}. The studio picks a strong frame for you unless you choose one.</p>
        <div className={`thumb-frame aspect-${String(p.brief.aspect ?? "9:16").replace(":", "-")}`}>
          <video
            ref={video}
            src={v.url}
            poster={v.thumbUrl || undefined}
            muted
            playsInline
            preload="auto"
            onLoadedMetadata={() => setReady(true)}
            aria-label={`Frame at ${fmtFrameTime(t)}`}
          />
        </div>
        <label className="thumb-scrub">
          <span className="sr-only">Moment in the video</span>
          <input type="range" min={0} max={max} step={0.04} value={t} onChange={(e) => setT(Number(e.target.value))} disabled={!!busy} aria-valuetext={fmtFrameTime(t)} />
          <span className="thumb-time" aria-hidden>
            {fmtFrameTime(t)} <span className="muted">/ {fmtFrameTime(max)}</span>
          </span>
        </label>
        <div className="thumb-acts">
          <button data-action="thumbnail.pick" type="button" className="btn btn-primary" onClick={() => save(t)} disabled={!!busy} data-autofocus>
            {busy === "pick" ? "Saving" : "Use this frame"}
          </button>
          <button data-action="thumbnail.pick" type="button" className="btn btn-quiet" onClick={() => save(null)} disabled={!!busy || !v.posterCustom}>
            {busy === "auto" ? "Picking" : "Let the studio pick"}
          </button>
          <Download url={v.thumbDownloadUrl} label={`the v${v.version} thumbnail`} variant="button" />
        </div>
      </div>
    </Sheet>
  );
}
